import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import { mapRoomPackage } from 'components/AccommodationKit/roomPackageUtils'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import AlwaysOnRateOption from 'components/OfferPage/AlwaysOn/AlwaysOnRateOption'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import uuidV4 from 'lib/string/uuidV4Utils'
import { useInView } from 'react-intersection-observer'
import * as Analytics from 'analytics/analytics'
import {
  arePropertyRatesLoading,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'

import PropertyAnytimeRateOption from './PropertyAnytimeRateOption'
import PropertyRateOption from './PropertyRateOption'
import PropertyRateOptionMerged from './PropertyRateOptionMerged'
import useGroupedPropertyRatesByRefundability from './useGroupedPropertyRatesByRefundability'
import type { RateView } from './singlePropertyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const MAX_RATES_TO_SHOW = 100

interface Props {
  shouldUseAccomContent: boolean
  property: App.Property.Property
  offer: App.HotelOffer | undefined
  bedbankOffer?: App.BedbankOffer
  propertyRoom: App.Property.Room | App.HotelPackage | App.BedbankPackage
  availableRooms: number | undefined
  displayRates: Array<RateView>
  roomFitsCapacity: boolean
  datesAvailable: boolean
  checkIn?: string
  rooms: Array<OfferPageRoom>
  flightDestination?: App.Airport
  isSelected: boolean
  isHighlighted?: boolean
  roomRateUnmatched?: boolean
  onViewPackageOptions: () => void
  duration?: number
  airport?: App.Airport
  roomListId: string
  roomPosition: number
  availableForPurchase?: boolean
  areAnytimeRates: boolean
  isLoading: boolean
}

function PropertyRoomOption(props: Props) {
  const {
    shouldUseAccomContent,
    property,
    offer,
    bedbankOffer,
    propertyRoom: roomPackage,
    availableRooms = 0,
    displayRates,
    roomFitsCapacity,
    datesAvailable,
    checkIn,
    rooms,
    flightDestination,
    isSelected,
    isHighlighted = false,
    roomRateUnmatched = true,
    onViewPackageOptions,
    duration,
    roomListId,
    roomPosition,
    availableForPurchase,
    areAnytimeRates,
    isLoading,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao.                                                                        */
  /************************************************************************************************/

  const [propertyRoom, lpcRoomType, bedbankRoomId] = mapRoomPackage<[App.Property.Room | undefined, App.PackageRoomType | undefined, string | undefined]>(
    roomPackage,
    propertyRoom => [propertyRoom, undefined, undefined],
    ({ roomType }) => [undefined, roomType, undefined],
    ({ roomId }) => [undefined, undefined, roomId],
  )
  // NB: TS can't quite tell, but between the three, we're guaranteed to get a room ID.
  const opaqueRoomId = (propertyRoom?.id ?? lpcRoomType?.id ?? bedbankRoomId)!
  // NB: `0`s become `undefined` so we can easily conditionally render `<BookingsCountLabel/>` later.
  const bookingsCount = (propertyRoom?.stats.booked ?? lpcRoomType?.stats.booked) || undefined
  const [showingMoreRates, showMoreRates] = useToggle()
  const offerPageDispatch = useOfferPageDispatch()
  const promoteRoom = isHighlighted || isSelected
  const promoteRoomWithSpecificRate = !isSelected && isHighlighted && !roomRateUnmatched
  const promoteRoomWithSomeRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !!displayRates.length
  const promoteRoomWithoutRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !displayRates.length
  const initialRatesToShow = promoteRoomWithSpecificRate ? 1 : 3

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const checkAvailability = !checkIn || !hasSelectedRooms(rooms)

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (isLoading) return undefined
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      const anytimeRatesActionState = areAnytimeRates ? 'available-on-dates-variant' : 'available-on-dates'
      if (offer && !offer.property?.useDynamicOccupancy) {
        if (!datesAvailable) return 'sold-out-on-dates'
        if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        if (checkAvailability) {
          if (roomFitsCapacity && datesAvailable) return anytimeRatesActionState
          if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        }
      }
      if (!checkIn) return anytimeRatesActionState
      if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
    }
  }, [areAnytimeRates, availableForPurchase, checkAvailability, checkIn, datesAvailable, isLoading, offer, roomFitsCapacity])

  const stayPayTier = useMemo(() => {
    return displayRates.map(({ lpcRate: view }) => {
      if (view && view.hotelAvailable && view.package.stayPay) {
        return view.package.stayPay.find(tier => tier.stayNights === duration && tier.freeNights > 0)
      }
    }).find(Boolean)
  }, [displayRates, duration])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setPropertyRoomId, roomId: propertyRoom?.id })
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: lpcRoomType })
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, propertyRoom?.id, lpcRoomType, bedbankRoomId])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback(() => {
    // If dates aren't set, the CTA will be "Select dates", so we'll use the default occupancy and
    // skip directly to selecting dates.
    toggleFilter((areAnytimeRates || hasSelectedRooms(rooms)) ? 'checkin' : 'capacity')
  }, [areAnytimeRates, rooms, toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayRates]) // regenerate id if packages change

  // Extract property rates for grouping
  const propertyRates = useMemo(() => {
    return displayRates.flatMap(({ propertyRate }) => {
      if (!propertyRate) return []
      return [propertyRate]
    })
  }, [displayRates])

  // AB Test: Merged package options
  // Note: Optimizely audience filters desktop users, but we also check viewport size
  // to handle window resizing (desktop user shrinking browser to mobile size)
  const isDesktop = useScreenSizeUp('desktop')
  const isMergedPackageOptionsEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croNewPackageColumnsTest, isDesktop)

  // Group property rates by refundability
  const groupedPropertyRates = useGroupedPropertyRatesByRefundability(propertyRates, isMergedPackageOptionsEnabled)

  // Calculate propertyTimezone similar to PropertyRateOption
  const propertyTimezone = (shouldUseAccomContent ? property.location.timezone : offer!.property.timezone) || 'UTC'
  // Track viewing packages section
  const { ref: packagesViewRef, inView: packagesInView } = useInView({
    threshold: 0.1, // Trigger when 10% visible
    triggerOnce: true, // Only trigger once
  })

  useEffect(() => {
    if (packagesInView) {
      Analytics.trackClientEvent({
        subject: 'packages-section',
        action: 'view',
        category: 'engagement',
        type: 'operational',
        optimizelyEventId: '4971739877212160',
        optimizelyEventKey: 'view_packages_section',
      })
    }
  }, [packagesInView])

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner kind="promotional-primary" icon={<LineInfoCircleIcon />}>
        <b>Special offer:</b> You get <Plural singular="night" count={stayPayTier.freeNights} withCount />{' '}
        free when staying <Plural singular="night" count={stayPayTier.stayNights} withCount /> on select packages.
      </MessageBanner>}
      <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
        {promoteRoom && <MessageBanner kind="promotional-primary" icon={<LineArrowDownIcon />}>
          {promoteRoomWithSpecificRate && <b>You were interested in this option.</b>}
          {promoteRoomWithSomeRates && <b>You were interested in this room. Book now to secure your room!</b>}
          {promoteRoomWithoutRates && <b>You were interested in this room.</b>}
        </MessageBanner>}
        <ScrollAnchor id={opaqueRoomId} vOffset={rem(80)} />
        <AccommodationRoomTile roomPackage={roomPackage}>
          <AccommodationRoomTileImage
            roomPackage={roomPackage}
            // Hide the bookmark button if there's no LPC package, as otherwise we'd wind up with a
            // mix of reservation _and_ bedbank room IDs. In the future, we'd want to migrate to
            // using accommodation room IDs.
            showBookmark={!!lpcRoomType}
          />
          <AccommodationRoomTileDetails
            roomPackage={roomPackage}
            urgencyLabels={<>
              {bookingsCount && <BookingsCountLabel bookings={bookingsCount} threshold="room" />}
              {checkIn && <RoomsRemainingLabel roomCount={availableRooms} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && lpcRoomType && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer!} roomTypeId={lpcRoomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          >
            <AccommodationRoomTileActions
              roomPackage={roomPackage}
              state={actionsState}
              isWalled={false}
              onChangeOccupancyClick={onChangeOccupancyClick}
              onChangeDatesClick={handleChangeDatesClick}
              onCheckAvailabilityClick={handleCheckAvailabilityClick}
            />
          </AccommodationRoomTileDetails>
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          {propertyRoom && areAnytimeRates && (
            <PropertyAnytimeRateOption propertyRoom={propertyRoom} />
          )}

          {/* Variant: Property rates grouped by board code and inclusions */}
          {isMergedPackageOptionsEnabled &&
            take(
              groupedPropertyRates || [],
              showingMoreRates ? MAX_RATES_TO_SHOW : initialRatesToShow,
            ).map((group, index) => {
              return (
                <PropertyRateOptionMerged
                  key={group.groupingKey}
                  lpcOffer={offer}
                  bedbankOffer={bedbankOffer}
                  rates={group.rates}
                  optionNumber={index + 1}
                  showOptionLabel={groupedPropertyRates.length > 1}
                  roomListId={roomListId}
                  roomPosition={roomPosition}
                  rateListId={rateListId}
                  propertyTimezone={propertyTimezone}
                  ref={index === 0 ? packagesViewRef : undefined}
                />
              )
            })}

          {/* Control: Separate rate options */}
          {!isMergedPackageOptionsEnabled && take(displayRates, showingMoreRates ? MAX_RATES_TO_SHOW : initialRatesToShow).map((view, index) => {
            if (view.lpcRate) {
              // This is an anytime rate. We don't show pricing at the rate level, even if `utm_bp`
              // is set.
              return <AlwaysOnRateOption
                key={view.lpcRate.package.uniqueKey}
                offer={offer!}
                optionNumber={index + 1}
                packageView={view.lpcRate}
                flightDestination={flightDestination}
                showOptionLabel={displayRates.length > 1}
                hidePricingArea
              />
            } else if (view.propertyRate) {
              return <PropertyRateOption
                key={view.propertyRate.rateId}
                lpcOffer={offer}
                bedbankOffer={bedbankOffer}
                rate={view.propertyRate}
                optionNumber={index + 1}
                showOptionLabel={displayRates.length > 1}
                roomListId={roomListId}
                roomPosition={roomPosition}
                rateListId={rateListId}
              />
            }
            return null
          })}

          {isLoading && <LoadingIndicator inline />}

          {/* Show more/less button for merged mode */}
          {isMergedPackageOptionsEnabled && (groupedPropertyRates?.length || 0) > initialRatesToShow && (
            <ShowMoreContent variant="medium" align="center">
              <SmallToggleButton
                on={showingMoreRates}
                onClick={showMoreRates}
              >
                {showingMoreRates ? 'Show less options' : 'Show more options'}
              </SmallToggleButton>
            </ShowMoreContent>
          )}

          {/* Show more/less button for control mode */}
          {!isMergedPackageOptionsEnabled && displayRates.length > initialRatesToShow && (
            <ShowMoreContent variant="medium" align="center">
              <SmallToggleButton
                on={showingMoreRates}
                onClick={showMoreRates}
              >
                {showingMoreRates ? 'Show less options' : 'Show more options'}
              </SmallToggleButton>
            </ShowMoreContent>
          )}
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  airport: state.airport,
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOption, preventRenderWithOpenFilter)
